import { PrimarySmallButton } from 'components/ReusableComponents/Button/SmallButtons'
import DataIsReprocessingBar from 'components/ReusableComponents/DataIsReprocessingBar'
import CustomHeader from 'components/ReusableComponents/HeaderTitles'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import Dropdown from 'components/ReusableComponents/ManageHotel/Dropdown'
import TextInput from 'components/ReusableComponents/ManageHotel/TextInput'
import Table, {
  Cell,
  Row,
  TitleCell,
  TitleRow
} from 'components/ReusableComponents/Table'
import Toggle from 'components/ReusableComponents/Toggle'
import BookingDetailsDialog from 'features/BookingDetailsDialog'
import BookingsDatepicker from 'features/Bookings/Datepicker'
import moment from 'moment'
import { useEffect, useState } from 'react'
import bookingsApi from 'services/api/bookings'
import { formatPrice } from 'utils/common'

import styles from './Bookings.module.scss'
import { bookedByFilters } from './constants'

const bookingStatusTypes = {
  Pending: 'Pending',
  Completed: 'Completed',
  Confirmed: 'Confirmed',
  InUse: 'In Use'
}

const Bookings = () => {
  const [isBookingsLoading, setIsBookingsLoading] = useState(true)
  const [isBookingsErred, setIsBookingsErred] = useState(false)

  const [bookings, setBookings] = useState([])

  const [lastName, setLastName] = useState('')
  const [firstName, setFirstName] = useState('')
  const [confirmationCode, setConfirmationCode] = useState('')
  const [checkIn, setCheckIn] = useState()
  const [checkOut, setCheckOut] = useState()
  const [bookedOn, setbookedOn] = useState(moment().format('YYYY-MM-DD'))
  const [cancelledOn, setcancelledOn] = useState()
  const [bookedBy, setBookedBy] = useState('any')

  const [activeBookingId, setActiveBookingId] = useState(null)
  const [isBookingDetailsDialogVisible, setIsBookingDetailsDialogVisible] =
    useState(false)

  const [sortIndex, setSortIndex] = useState(0)
  const [sortAscending, setSortAscending] = useState(true)

  const [groupBookingsWithSameName, setGroupBookingsWithSameName] = useState(true)

  const [hasBookingsWithSameName, setHasBookingsWithSameName] = useState(false)

  function handleBookingClick(bookingId) {
    setIsBookingDetailsDialogVisible(true)
    setActiveBookingId(bookingId)
  }

  useEffect(() => {
    if (bookings?.length > 1) {
      const hasBookingsWithSameName =
        bookings?.reduce((acc, cur) => {
          if (acc.find(a => a.firstName + a.lastName === cur.firstName + cur.lastName)) {
            return acc
          }
          return [...acc, cur]
        }, []).length > 1
      setHasBookingsWithSameName(hasBookingsWithSameName)
    }
  }, [bookings])

  function handleSearch() {
    setIsBookingsLoading(true)
    setIsBookingsErred(false)

    setBookings([])

    bookingsApi
      .list({
        firstName,
        lastName,
        confirmationCode,
        checkIn,
        checkOut,
        bookingStartDate: bookedOn,
        bookingEndDate: bookedOn,
        cancelledOn,
        bookedBy
      })
      .then(bookings => {
        setBookings(bookings)
        setIsBookingsErred(false)
      })
      .catch(() => {
        setIsBookingsErred(true)
      })
      .finally(() => {
        setIsBookingsLoading(false)
      })
  }

  useEffect(() => {
    setIsBookingsLoading(true)

    bookingsApi
      .list({
        bookingStartDate: moment().format('YYYY-MM-DD'),
        bookingEndDate: moment().format('YYYY-MM-DD'),
        bookedBy: 'any'
      })
      .then(bookings => {
        setBookings(bookings)
        setIsBookingsErred(false)
      })
      .catch(() => {
        setIsBookingsErred(true)
      })
      .finally(() => {
        setIsBookingsLoading(false)
      })
  }, [])

  function handleInputKeyPress(event) {
    if (event.key === 'Enter') {
      handleSearch()
    }
  }

  function handleTitleClick(i) {
    const sortableIndexes = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]

    if (sortIndex === i) {
      setSortAscending(d => !d)
    }

    if (sortableIndexes.includes(i)) {
      setSortIndex(i)
    }
  }

  function sortResults(results) {
    const sortedData = results.sort((a, b) => {
      switch (sortIndex) {
        case 0:
          if (a.fullName === b.fullName) return 0

          return a?.fullName?.toUpperCase() > b?.fullName?.toUpperCase() ? 1 : -1

        case 1:
          return new Date(a.checkIn) - new Date(b.checkIn)

        case 2:
          return new Date(a.checkOut) - new Date(b.checkOut)

        case 3:
          return new Date(a.bookingDate) - new Date(b.bookingDate)

        case 4:
          return a.guestCount - b.guestCount

        case 5:
          return a.roomNumber - b.roomNumber

        case 6:
          return a.rateCode - b.rateCode

        case 7:
          if (a.agencyParentName === null) return -1

          if (a.agencyParentName === b.agencyParentName) return 0

          return a.agencyParentName - b.agencyParentName

        case 8:
          if (a.bookingStatus === b.bookingStatus) return 0

          return a.bookingStatus > b.bookingStatus ? 1 : -1

        case 9:
          return a.nightlyRate - b.nightlyRate

        case 10:
          return a.totalRate - b.totalRate

        default:
          return 0
      }
    })

    return sortAscending ? sortedData : [...sortedData].reverse()
  }

  return (
    <div>
      {isBookingDetailsDialogVisible && (
        <BookingDetailsDialog
          bookingId={activeBookingId}
          onClose={() => setIsBookingDetailsDialogVisible(false)}
        />
      )}
      <CustomHeader title="Bookings" width={1080} isFixed />
      <div className={styles.wrapper}>
        <div className={styles.bar}>
          <div className={styles.container}>
            <div className={styles.filters}>
              <TextInput
                className={styles.textInput}
                label="Last name"
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                type="Search"
                onKeyPress={handleInputKeyPress}
              />
              <TextInput
                className={styles.textInput}
                label="First name"
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                type="Search"
                onKeyPress={handleInputKeyPress}
              />
              <TextInput
                className={styles.textInput}
                label="Confirmation #"
                value={confirmationCode}
                onChange={e => setConfirmationCode(e.target.value)}
                type="Search"
                onKeyPress={handleInputKeyPress}
              />
              <BookingsDatepicker
                label="Check-in"
                selected={checkIn ? moment(checkIn).toDate() : null}
                onChange={date =>
                  date ? setCheckIn(moment(date).format('YYYY/MM/DD')) : setCheckIn()
                }
              />
              <BookingsDatepicker
                label="Check-out"
                selected={checkOut ? moment(checkOut).toDate() : null}
                onChange={date =>
                  date ? setCheckOut(moment(date).format('YYYY/MM/DD')) : setCheckOut()
                }
              />
              <BookingsDatepicker
                label="Booked on"
                selected={bookedOn ? moment(bookedOn).toDate() : null}
                onChange={date =>
                  date ? setbookedOn(moment(date).format('YYYY/MM/DD')) : setbookedOn()
                }
              />
              <BookingsDatepicker
                label="Cancelled on"
                selected={cancelledOn ? moment(cancelledOn).toDate() : null}
                onChange={date =>
                  date
                    ? setcancelledOn(moment(date).format('YYYY/MM/DD'))
                    : setcancelledOn()
                }
              />
              <div className={styles.headerDropdownContainer}>
                <span className={styles.headerDropdownTitle}>Booked by</span>
                <Dropdown
                  title="select message type"
                  options={bookedByFilters}
                  onChange={e => setBookedBy(e.target.value)}
                  value={bookedBy}
                  className={styles.headerDropdown}
                />
              </div>

              <PrimarySmallButton className={styles.searchButton} onClick={handleSearch}>
                Search
              </PrimarySmallButton>
            </div>
          </div>
        </div>
        <DataIsReprocessingBar marginTop={20} marginBottom={40} />
        {isBookingsLoading && (
          <div className={styles.wrapper}>
            <LoadingIndicator />
          </div>
        )}
        {isBookingsErred && (
          <p className={styles.noResults}>
            Something went wrong while getting the bookings
          </p>
        )}
        {!isBookingsErred && !isBookingsLoading && !bookings && (
          <p className={styles.noResults}>No bookings found for selected filters</p>
        )}
        {hasBookingsWithSameName &&
          !isBookingsLoading &&
          !isBookingsErred &&
          bookings?.length > 0 && (
            <Toggle
              className={styles.groupToggle}
              label="Group bookings with the same name"
              checked={groupBookingsWithSameName}
              handleChange={e => setGroupBookingsWithSameName(e.target.checked)}
            />
          )}
        {!isBookingsLoading && !isBookingsErred && bookings?.length > 0 && (
          <Table
            widths={[210, 70, 70, 100, 60, 50, 120, 120, 100, 90, 50]}
            sortIndex={sortIndex}
            sortDirection={sortAscending ? 'up' : 'down'}
            handleTitleCellClick={handleTitleClick}
          >
            <TitleRow>
              <TitleCell>Guest name</TitleCell>
              <TitleCell>Check-in</TitleCell>
              <TitleCell>Check-out</TitleCell>
              <TitleCell>Booking date</TitleCell>
              <TitleCell>Guests</TitleCell>
              <TitleCell>Room</TitleCell>
              <TitleCell>Rate code</TitleCell>
              <TitleCell>Agency</TitleCell>
              <TitleCell>Status</TitleCell>
              <TitleCell>Nightly rate</TitleCell>
              <TitleCell>Total rate</TitleCell>
            </TitleRow>
            {sortResults(
              bookings.reduce((acc, cur) => {
                if (!groupBookingsWithSameName) {
                  return [...acc, cur]
                }

                if (
                  acc.find(a => a.firstName + a.lastName === cur.firstName + cur.lastName)
                ) {
                  return acc
                }
                return [...acc, cur]
              }, [])
            ).map(b => (
              <Row className={styles.row} key={b.bookingId}>
                <Cell className={styles.fullNameCell}>
                  <button
                    className={styles.fullNameButton}
                    onClick={() => handleBookingClick(b.bookingId)}
                  >
                    {b.fullName}
                  </button>
                </Cell>
                <Cell>{b.checkIn ? moment(b.checkIn).format('MMM D') : '-'}</Cell>
                <Cell>{b.checkOut ? moment(b.checkOut).format('MMM D') : '-'}</Cell>
                <Cell>
                  {b.bookingDate ? moment(b.bookingDate).format('MMM D, YYYY') : '-'}
                </Cell>
                <Cell>{b.guestCount}</Cell>
                <Cell>{b.roomNumber}</Cell>
                <Cell>
                  <span
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '110px',
                      paddingRight: '5px',
                      boxSizing: 'border-box'
                    }}
                    title={b.rateCode}
                  >
                    {b.rateCode}
                  </span>
                </Cell>
                <Cell overflow="hidden" className={styles.noOverflow}>
                  <span title={b.agencyParentName} id="my-element">
                    {b.agencyParentName}
                  </span>
                </Cell>
                <Cell>{bookingStatusTypes[b.bookingStatus] || b.bookingStatus}</Cell>
                <Cell>{b.nightlyRate ? formatPrice(b.nightlyRate) : '-'}</Cell>
                <Cell>{b.nightlyRate ? formatPrice(b.totalRate) : '-'}</Cell>
              </Row>
            ))}
          </Table>
        )}
      </div>
    </div>
  )
}

export default Bookings
